import initSqlJs, { SQL } from '@jlongster/sql.js';
import { SQLiteFS } from 'absurd-sql';
import IndexedDBBackend from 'absurd-sql/dist/indexeddb-backend';
import { CompiledQuery } from 'kysely';

import schema from './schema.sql';
import { SQLiteDatabase } from './types';

const DATA_DIR = '/data';

export class Database {
  filename: string;

  #setupPromise: Promise<void>;

  #sql: SQL | undefined;

  #fs: SQLiteFS | undefined;

  #db: SQLiteDatabase | undefined;

  constructor(filename: string) {
    this.filename = filename;
    this.#setupPromise = this.setup();
  }

  async ready() {
    await this.#setupPromise;
  }

  exec<
    Q extends CompiledQuery<unknown>,
    R = Q extends CompiledQuery<infer Result> ? Result : never,
  >(q: Q): readonly R[] {
    const results = this.execRaw(q.sql, q.parameters);

    if (results.length === 0) {
      return [];
    }

    const result = results[0]; // Only executing one query, so only one result
    const { columns } = result;
    const rows = result.values;

    const resultObjects: R[] = [];

    for (const row of rows) {
      const obj: Record<string, unknown> = {};
      for (let i = 0; i < row.length; i++) {
        obj[columns[i]] = row[i];
      }
      resultObjects.push(obj as R);
    }

    return resultObjects;
  }

  execRaw(sql: string, parameters: readonly unknown[]) {
    if (!this.#db) throw new Error('DB not ready');

    return this.#db.exec(sql, parameters);
  }

  async reset() {
    if (!this.#fs || !this.#sql) {
      throw new Error('Database is not initialized yet!');
    }

    this.#sql.FS.unlink(this.databaseFilePath());
  }

  async clearAddressTables() {
    // Get a list of all user-created tables in the database
    const schemaResult = this.execRaw(
      `select name from sqlite_schema where name not like 'sqlite%'`,
      [],
    );
    const addressTables = ['address', 'utxo'];
    const tables = schemaResult[0].values
      .map((v) => v[0])
      .filter((v) => addressTables.includes(v as string));

    // Temporary, to allow dropping tables in any order. This will be reversed
    // when the schema is loaded afterward.
    this.execRaw(`pragma foreign_keys = off`, []);

    // Drop all tables in the schema
    for (const table of tables) {
      this.execRaw(`drop table ${table}`, []);
    }

    // Load the schema, as if it were the first time
    this.loadSchema();
  }

  async clearAllTables() {
    // Get a list of all user-created tables in the database
    const schemaResult = this.execRaw(
      `select name from sqlite_schema where name not like 'sqlite%'`,
      [],
    );
    const tables = schemaResult[0].values.map((v) => v[0]);

    // Temporary, to allow dropping tables in any order. This will be reversed
    // when the schema is loaded afterward.
    this.execRaw(`pragma foreign_keys = off`, []);

    // Drop all tables in the schema
    for (const table of tables) {
      this.execRaw(`drop table ${table}`, []);
    }

    // Load the schema, as if it were the first time
    this.loadSchema();
  }

  private databaseFilePath() {
    return `${DATA_DIR}/${this.filename}`;
  }

  private async setup() {
    const sql = await initSqlJs({ locateFile: (file: unknown) => file });
    const fs = new SQLiteFS(sql.FS, new IndexedDBBackend());

    sql.register_for_idb(fs);
    sql.FS.mkdir(DATA_DIR);
    sql.FS.mount(fs, {}, DATA_DIR);

    this.#sql = sql;
    this.#fs = fs;
    this.initDatabase();
  }

  private initDatabase() {
    if (!this.#sql) {
      throw new Error('SQL not initialized yet!');
    }

    const DB = this.#sql.Database as typeof SQLiteDatabase;
    this.#db = new DB(this.databaseFilePath(), { filename: true });

    this.loadSchema();
  }

  private loadSchema() {
    try {
      this.execRaw(schema, []);
    } catch (error: unknown) {
      // eslint-disable-next-line no-console
      console.error('Error executing database schema:');
      // eslint-disable-next-line no-console
      console.error(error);
    }
  }
}

// If the schema is updated, reset the database and reload the page
if (module.hot) {
  module.hot.accept('./schema.sql', window.$reset);
}
